import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Discounts = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Discount codes let you offer reduced pricing on Event tickets. They
            are ideal for early bird promotions, sponsor giveaways, group
            discounts, or rewarding loyal community Members.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/events/event-page.png'
          alt={t`Event page showing the checkout flow where attendees can apply discount codes during ticket purchase`}
          caption={t`Attendees apply discount codes during the checkout flow on the Event page`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection
          id='discount-configuration'
          title={t`Discount Configuration`}
        >
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='ribbon-filled'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Creating a discount code
                  </Paragraph>{' '}
                  — Open{' '}
                  <Link href='/docs/events/settings'>Event Settings</Link> and
                  go to the Tickets section. Click{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    Add Discount Code
                  </Paragraph>{' '}
                  to create a new code. Enter a code name that attendees will
                  type during checkout (for example, EARLYBIRD or COMMUNITY20).
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='money'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Discount types
                  </Paragraph>{' '}
                  — Choose between a percentage-based discount (e.g., 20% off)
                  or a fixed-amount discount (e.g., $10 off). Percentage
                  discounts are applied to the tier price, while fixed-amount
                  discounts subtract a flat amount from the total.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Usage limits
                  </Paragraph>{' '}
                  — Set a maximum number of times the code can be used. For
                  example, you can create a code that is limited to the first 50
                  uses, making it useful for limited-time promotions. Once the
                  limit is reached, the code automatically stops working.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='ticket-diagonal'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Tier restrictions
                  </Paragraph>{' '}
                  — Optionally restrict a discount code to specific{' '}
                  <Link href='/docs/events/tiers'>ticketing tiers</Link>. For
                  example, you can create a 20% early bird code that only
                  applies to General Admission tickets but not to VIP tickets.
                  If no restriction is set, the code applies to all paid tiers.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='applying-discounts' title={t`Applying Discounts`}>
          <Paragraph size='$5'>
            <Trans>
              Attendees apply discount codes during the{' '}
              <Link href='/docs/events/purchase-flow'>checkout flow</Link>. The
              discount is reflected in the payment breakdown before they confirm
              their purchase, so attendees can see exactly how much they are
              saving.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Discounts
